import React, { useEffect } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
// Components
import FormSubmit from '../form/formSubmit';
import FormUtils from '../../utils/forms';

export enum DialogTextColor {
    RED = 'red',
}
interface DialogProps extends WithTranslation {
    onRef?: any;
    className?: string;
    btnTitle?: string;
    description: string;
    dialogTitle?: string;
    dialogTitleColor?: DialogTextColor;
    btnOKText?: string;
    btnOKColor?: DialogTextColor;
    onOK?: Function;
    disableOK?: boolean;
    btnKOText?: string;
    btnKOColor?: DialogTextColor;
    onKO?: Function;
    customButton?: JSX.Element;
    formComponent?: JSX.Element;
    isOpen?: boolean;
    disableEscapeManual?: boolean;
    noKOButton?: boolean;
    noOKButton?: boolean;
}

const DialogView = (props: DialogProps): JSX.Element => {
    const classes = useStyles();
    const {
        t,
        className,
        customButton,
        formComponent,
        btnTitle,
        dialogTitle,
        dialogTitleColor,
        description,
        btnOKText,
        btnOKColor,
        disableOK,
        btnKOText,
        btnKOColor,
        onOK,
        onKO,
        onRef,
        disableEscapeManual,
        noKOButton,
        noOKButton,
    } = props;

    const [isOpen, setIsOpen] = React.useState<boolean>(props.isOpen ?? false);
    const handleClickOpen = (e): void => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsOpen(true);
    };
    const handleClose = (e, reason?): void => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (disableEscapeManual && reason) {
            if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
                return;
            }
        }
        setIsOpen(false);
    };

    useEffect(() => {
        if (onRef) {
            onRef({ showDialog: handleClickOpen, closeDialog: handleClose });
        }
        return () => {
            if (onRef) {
                onRef(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRef]);

    const handleCloseOK = (e): void => {
        e.preventDefault();
        e.stopPropagation();
        const fields = FormUtils.getFields(e.target);

        if (onOK) {
            onOK(fields);
        }
        handleClose(e);
    };

    const handleCloseKO = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        if (onKO) {
            onKO();
        }
        handleClose(e);
    };

    //TODO: Style
    const pre: boolean = description.includes('\n') || description.includes('\t');
    return (
        <span>
            {customButton && <span onClick={handleClickOpen}>{customButton}</span>}
            {!customButton && btnTitle && (
                <Button variant="contained" color="primary" onClick={handleClickOpen}>
                    {btnTitle}
                </Button>
            )}
            <Dialog
                open={isOpen}
                disableEscapeKeyDown={disableEscapeManual}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className={className}
            >
                <FormSubmit debounceSubmit={{ callback: handleCloseOK }} stopPropagation={true}>
                    <DialogTitle id="alert-dialog-title" className={classes[dialogTitleColor]}>
                        {dialogTitle || btnTitle}
                    </DialogTitle>
                    <DialogContent>
                        {description && (
                            <DialogContentText
                                style={pre ? { whiteSpace: 'pre-wrap' } : {}}
                                id="alert-dialog-description"
                            >
                                {description}
                            </DialogContentText>
                        )}
                        {formComponent}
                    </DialogContent>
                    <DialogActions>
                        {!noKOButton && (
                            <Button onClick={handleCloseKO} color="primary" className={classes[btnKOColor]}>
                                {btnKOText || t('common:back')}
                            </Button>
                        )}
                        {!noOKButton && (
                            <Button
                                type="submit"
                                color="secondary"
                                className={classes[btnOKColor]}
                                disabled={disableOK}
                            >
                                {btnOKText || t('common:validate')}
                            </Button>
                        )}
                    </DialogActions>
                </FormSubmit>
            </Dialog>
        </span>
    );
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        red: {
            color: theme.palette.danger.contrastText,
        },
    }),
);

export default withTranslation(['dialog', 'common'])(DialogView);
